import { useEffect, useRef, useState } from 'react';

type MultiSelectProps = {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
};

export default function MultiSelect({ label, options, selected, onChange }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (option: string) => {
    onChange(selected.includes(option) ? selected.filter((item) => item !== option) : [...selected, option]);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-[12px] transition-colors ${
          selected.length
            ? 'border-primary-500/60 bg-primary-500/10 text-primary-400'
            : 'border-background-300 bg-background-100 text-foreground-700 hover:border-background-400'
        }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[9px] text-background-50">
            {selected.length}
          </span>
        )}
        <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 w-52 animate-soft-fade rounded-lg border border-background-300 bg-background-100 p-2 shadow-none">
          <div className="max-h-64 overflow-y-auto">
            {options.map((option) => {
              const checked = selected.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggle(option)}
                  className="flex w-full cursor-pointer items-center justify-between gap-2 rounded px-2.5 py-2 text-left text-[12px] text-foreground-700 transition-colors hover:bg-background-200"
                >
                  <span className="truncate">{option}</span>
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] ${
                      checked
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : 'border-background-400 text-transparent'
                    }`}
                  >
                    <i className="ri-check-line" />
                  </span>
                </button>
              );
            })}
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full cursor-pointer rounded px-2.5 py-2 text-left text-[11px] text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-800"
            >
              清除该项筛选
            </button>
          )}
        </div>
      )}
    </div>
  );
}